/**
 * Client-side file validation utility — sic-frontend
 *
 * Validates file size and real file type via magic bytes BEFORE sending to the server.
 * This provides early feedback to the user and reduces unnecessary network requests.
 *
 * Security note: This is a UX layer — the backend is the authoritative validation point.
 * Never rely solely on client-side validation.
 *
 * OWASP A04:2021 / CWE-434 mitigation
 */

const MAX_FILE_SIZE_BYTES = 12 * 1024 * 1024; // 12MB

/**
 * Magic bytes signatures for allowed file types.
 * Each entry maps a MIME type to one or more byte sequences that identify it.
 */
const FILE_SIGNATURES: { mime: string; bytes: number[] }[] = [
  { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47] },
  { mime: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46] }, // %PDF
  { mime: 'video/webm', bytes: [0x1a, 0x45, 0xdf, 0xa3] }, // EBML header (WebM/MKV)
];

export type FileValidationError = 'FILE_TOO_LARGE' | 'INVALID_FILE_TYPE' | null;

/**
 * Validates ONLY the file type via magic bytes (no size check).
 * Use this before compression so large images are not rejected prematurely.
 *
 * @param file - The File object to validate
 * @returns null if type is valid, or 'INVALID_FILE_TYPE'
 */
export async function validateFileTypeOnly(
  file: File,
): Promise<FileValidationError> {
  const buffer = await file.slice(0, 8).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const isValidType = FILE_SIGNATURES.some(({ bytes: sig }) =>
    sig.every((byte, index) => bytes[index] === byte),
  );

  if (!isValidType) {
    return 'INVALID_FILE_TYPE';
  }

  return null;
}

/**
 * Validates ONLY the file type for an array of files.
 * Returns the first invalid file found, or null if all are valid.
 */
export async function validateFilesTypeOnly(
  files: File[],
): Promise<{ error: FileValidationError; fileName: string } | null> {
  for (const file of files) {
    const error = await validateFileTypeOnly(file);
    if (error) {
      return { error, fileName: file.name };
    }
  }
  return null;
}

/**
 * Validates a single file before upload by checking:
 * 1. File size does not exceed 12MB
 * 2. Real file type via magic bytes (first bytes of the file buffer)
 *
 * @param file - The File object from an <input type="file"> element
 * @returns null if valid, or a FileValidationError string describing the problem
 */
export async function validateFileBeforeUpload(
  file: File,
): Promise<FileValidationError> {
  // 1. Validate size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'FILE_TOO_LARGE';
  }

  // 2. Read first 8 bytes to check magic bytes
  const buffer = await file.slice(0, 8).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const isValidType = FILE_SIGNATURES.some(({ bytes: sig }) =>
    sig.every((byte, index) => bytes[index] === byte),
  );

  if (!isValidType) {
    return 'INVALID_FILE_TYPE';
  }

  return null;
}

/**
 * Validates an array of files and returns the first validation error found.
 * Returns null if all files are valid.
 *
 * @param files - Array of File objects to validate
 * @returns null if all valid, or { error, fileName } for the first invalid file
 */
export async function validateFilesBeforeUpload(
  files: File[],
): Promise<{ error: FileValidationError; fileName: string } | null> {
  for (const file of files) {
    const error = await validateFileBeforeUpload(file);
    if (error) {
      return { error, fileName: file.name };
    }
  }
  return null;
}
